/* eslint-disable react-hooks/exhaustive-deps */
import * as Sentry from '@sentry/nextjs'
import { useRouter } from 'next/router'
import {
  Dispatch,
  ReactNode,
  SetStateAction,
  createContext,
  useContext,
  useEffect,
  useState,
} from 'react'
import { toast } from 'react-toastify'
import { apiV2 } from 'src/services/api'
import { autenticado } from '../../services/auth'
import { ContextoGlobal } from '../contextoGlobal'
import { Favorito } from '../types/Favorito'

interface FavoriteProviderProps {
  children: ReactNode
}

interface IFavoriteContextData {
  favorites: Favorito[]
  favorite: (
    url_produto: string,
    produto_id: number,
    cor: string,
    voltagem: string,
    favoritoStoreKey: number
  ) => void
  setFavorites: Dispatch<SetStateAction<Favorito[]>>
  startFavorites: () => void
}

const FavoriteContext = createContext({} as IFavoriteContextData)

function FavoriteProvider({ children }: FavoriteProviderProps) {
  const route = useRouter()
  const context = useContext(ContextoGlobal)
  const { start, end } = context
  const [favorites, setFavorites] = useState<Favorito[]>([])

  useEffect(() => {
    if (!favorites.length) {
      startFavorites()
    }
  }, [])

  const startFavorites = async () => {
    if (!autenticado()) {
      return setFavorites!([])
    }
    try {
      const { data } = await apiV2.get('/canais/favoritos')
      setFavorites(data)
    } catch (error: any) {
      toast.error(error.response.data.message)
    }
  }

  const favorite = async (
    url_produto: string,
    produto_id: number,
    cor: string,
    voltagem: string,
    favoritoStoreKey: number
  ) => {
    if (!autenticado()) {
      return route.push('/login')
    }

    if (!cor || !voltagem) {
      Sentry.captureException(
        new Error(
          `Produto ${produto_id} sem cor ou voltagem definida. URL: ${url_produto}`
        )
      )
      toast.error('Ocorreu um erro ao favoritar o produto.')
      return
    }

    start()

    const data = {
      id: favorites[favoritoStoreKey]?.id,
      url_produto: url_produto,
      produto_id: produto_id,
      cor: cor,
      voltagem: voltagem,
    }

    const request =
      favoritoStoreKey >= 0
        ? apiV2.delete(`/canais/favoritos/${data.id}`)
        : apiV2.post('/canais/favoritos', data)

    try {
      const { data } = await request

      if (!(favoritoStoreKey >= 0)) {
        setFavorites!([...favorites, data])
        toast.success('Produto adicionado aos favoritos.')
        return
      }

      const favoritoComExclusao = [
        ...favorites.slice(0, favoritoStoreKey),
        ...favorites.slice(favoritoStoreKey + 1),
      ]
      setFavorites!(favoritoComExclusao)
      toast.success('Produto removido dos favoritos.')
    } catch (error: any) {
      toast.error(error.response.data.message)
    } finally {
      end()
    }
  }

  return (
    <FavoriteContext.Provider
      value={{
        favorites,
        favorite,
        setFavorites,
        startFavorites,
      }}
    >
      {children}
    </FavoriteContext.Provider>
  )
}

function useFavorite() {
  const context = useContext(FavoriteContext)

  return context
}

export { FavoriteProvider, useFavorite }
